import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Save, Trash2, Plus, Star, Zap, Heart, Loader2, X, GripVertical } from "lucide-react";

const CATEGORY_CFG = {
  ems: { label: "Électrostimulation EMS", Icon: Zap, color: "fegreen" },
  massage: { label: "Massages & Drainage", Icon: Heart, color: "amber" },
};

export default function AdminContent() {
  const [packs, setPacks] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [newT, setNewT] = useState({ author: "", text: "", rating: 5, years_ago: "" });
  const [activeCategory, setActiveCategory] = useState("ems");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([
        api.get("/admin/packs"),
        api.get("/testimonials"),
      ]);
      setPacks(p.data);
      setTestimonials(t.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const savePack = async (id, partial) => {
    try {
      await api.patch(`/admin/packs/${id}`, partial);
      toast.success("Pack mis à jour");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addTestimonial = async () => {
    if (!newT.author || !newT.text) {
      toast.error("Auteur et texte requis");
      return;
    }
    try {
      await api.post("/admin/testimonials", newT);
      toast.success("Témoignage ajouté");
      setNewT({ author: "", text: "", rating: 5, years_ago: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeTestimonial = async (id) => {
    if (!window.confirm("Supprimer ce témoignage ?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Supprimé");
    load();
  };

  // Group packs by category
  const { byCategory, counts } = useMemo(() => {
    const groups = { ems: [], massage: [] };
    for (const p of packs) {
      const cat = p.category === "massage" ? "massage" : "ems";
      groups[cat].push(p);
    }
    return {
      byCategory: groups,
      counts: { ems: groups.ems.length, massage: groups.massage.length },
    };
  }, [packs]);

  const currentPacks = byCategory[activeCategory] || [];

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-2">— Gestion contenu</p>
      <h1 className="text-3xl lg:text-4xl font-bold text-fegray-dark mb-2">Offres &amp; tarifs</h1>
      <p className="text-sm text-fegray mb-8">Modifiez le nom, la description, le prix, le nombre de séances et les avantages affichés sur le site.</p>

      <section className="mb-14">
        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="pack-category-tabs">
          {Object.entries(CATEGORY_CFG).map(([key, cfg]) => {
            const active = activeCategory === key;
            const Icon = cfg.Icon;
            return (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                data-testid={`pack-tab-${key}`}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                  active
                    ? key === "ems"
                      ? "bg-fegreen text-white shadow"
                      : "bg-amber-500 text-white shadow"
                    : "bg-white border border-gray-200 text-fegray-dark hover:border-fegreen"
                }`}
              >
                <Icon className="w-4 h-4" /> {cfg.label}
                <span className={`text-[10px] font-extrabold rounded-full px-2 py-0.5 ${
                  active ? "bg-white/30 text-white" : "bg-fegreen-light text-fegreen"
                }`}>{counts[key] || 0}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-fegreen mx-auto" /></div>
        ) : currentPacks.length === 0 ? (
          <div className="fe-card p-10 text-center text-fegray">Aucun pack dans cette catégorie.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="packs-grid">
            {currentPacks.map((p) => (
              <PackEditor key={p.id} pack={p} onSave={(partial) => savePack(p.id, partial)} />
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-xl font-bold text-fegray-dark mb-5">Témoignages</h2>

        <div className="fe-card p-6 mb-6" data-testid="add-testimonial-form">
          <p className="text-xs uppercase tracking-wider text-fegreen font-bold mb-4">Ajouter un témoignage</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              placeholder="Nom de l'auteur"
              value={newT.author}
              onChange={(e) => setNewT({ ...newT, author: e.target.value })}
              className="bg-white border border-gray-200 rounded-xl px-4 py-3 text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
            />
            <input
              placeholder="Date (ex: il y a 2 ans)"
              value={newT.years_ago}
              onChange={(e) => setNewT({ ...newT, years_ago: e.target.value })}
              className="bg-white border border-gray-200 rounded-xl px-4 py-3 text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
            />
            <textarea
              rows={3}
              placeholder="Texte du témoignage"
              value={newT.text}
              onChange={(e) => setNewT({ ...newT, text: e.target.value })}
              className="md:col-span-2 bg-white border border-gray-200 rounded-xl px-4 py-3 text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
            />
            <button onClick={addTestimonial} className="btn-green md:col-span-2 justify-self-start" data-testid="submit-testimonial">
              <Plus className="w-4 h-4" /> Ajouter
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {testimonials.map((t) => (
            <div key={t.id} className="fe-card p-6">
              <div className="flex gap-0.5 mb-3">
                {[...Array(t.rating)].map((_, j) => (
                  <Star key={j} className="w-3.5 h-3.5 fill-fegreen text-fegreen" />
                ))}
              </div>
              <p className="text-sm text-fegray-dark mb-4 line-clamp-4">{t.text}</p>
              <div className="flex justify-between items-end pt-3 border-t border-gray-100">
                <div>
                  <div className="font-semibold text-sm text-fegray-dark">{t.author}</div>
                  <div className="text-xs text-fegray/60">{t.years_ago}</div>
                </div>
                <button onClick={() => removeTestimonial(t.id)} className="text-red-500 hover:text-red-700">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function PackEditor({ pack, onSave }) {
  const [edit, setEdit] = useState({
    name: pack.name,
    description: pack.description,
    nb_seances: pack.nb_seances,
    price: pack.price,
    popular: pack.popular,
    category: pack.category || "ems",
    features: Array.isArray(pack.features) ? [...pack.features] : [],
  });
  const [saving, setSaving] = useState(false);
  const [newFeature, setNewFeature] = useState("");

  const dirty =
    edit.name !== pack.name ||
    edit.description !== pack.description ||
    edit.nb_seances !== pack.nb_seances ||
    edit.price !== pack.price ||
    edit.popular !== pack.popular ||
    edit.category !== (pack.category || "ems") ||
    JSON.stringify(edit.features) !== JSON.stringify(pack.features || []);

  const submit = async () => {
    setSaving(true);
    try {
      await onSave(edit);
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    const v = newFeature.trim();
    if (!v) return;
    setEdit({ ...edit, features: [...edit.features, v] });
    setNewFeature("");
  };
  const removeFeature = (i) => {
    setEdit({ ...edit, features: edit.features.filter((_, idx) => idx !== i) });
  };
  const moveFeature = (i, dir) => {
    const arr = [...edit.features];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setEdit({ ...edit, features: arr });
  };

  const accentBorder = edit.category === "massage" ? "border-amber-200" : "border-fegreen/20";
  const pricePerSession = edit.nb_seances > 0 ? (edit.price / edit.nb_seances).toFixed(2) : "—";

  return (
    <div className={`fe-card p-6 border-2 ${accentBorder}`} data-testid={`pack-editor-${pack.id}`}>
      <div className="flex items-center justify-between mb-4">
        <p className="text-[10px] uppercase tracking-wider text-fegreen font-bold font-mono">{pack.id}</p>
        {edit.popular && (
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-fegreen text-white font-bold">
            <Star className="w-3 h-3 inline-block fill-white mr-1" /> Populaire
          </span>
        )}
      </div>

      <label className="block text-[10px] uppercase tracking-wider text-fegray font-bold mb-1">Nom commercial</label>
      <input
        value={edit.name}
        onChange={(e) => setEdit({ ...edit, name: e.target.value })}
        data-testid={`pack-name-${pack.id}`}
        className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 mb-3 text-fegray-dark font-bold focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
      />

      <label className="block text-[10px] uppercase tracking-wider text-fegray font-bold mb-1">Description</label>
      <textarea
        rows={2}
        value={edit.description}
        onChange={(e) => setEdit({ ...edit, description: e.target.value })}
        data-testid={`pack-description-${pack.id}`}
        className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 mb-3 text-sm text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
      />

      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-fegray font-bold">Séances</label>
          <input type="number" min={1} value={edit.nb_seances}
            data-testid={`pack-nbseances-${pack.id}`}
            onChange={(e) => setEdit({ ...edit, nb_seances: parseInt(e.target.value || "0", 10) })}
            className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-fegray-dark focus:border-fegreen focus:outline-none" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-fegray font-bold">Prix €</label>
          <input type="number" step="0.01" value={edit.price}
            data-testid={`pack-price-${pack.id}`}
            onChange={(e) => setEdit({ ...edit, price: parseFloat(e.target.value || "0") })}
            className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-fegray-dark focus:border-fegreen focus:outline-none" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-fegray font-bold">Prix/séance</label>
          <div className="w-full bg-fegray-light border border-gray-200 rounded-xl px-3 py-2 text-fegray-dark font-bold text-center">
            {pricePerSession}€
          </div>
        </div>
      </div>

      <div className="mb-4">
        <label className="text-[10px] uppercase tracking-wider text-fegray font-bold">Famille de service</label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          {Object.entries(CATEGORY_CFG).map(([key, cfg]) => {
            const Icon = cfg.Icon;
            const active = edit.category === key;
            return (
              <button
                type="button"
                key={key}
                onClick={() => setEdit({ ...edit, category: key })}
                data-testid={`pack-cat-${pack.id}-${key}`}
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border-2 text-xs font-bold transition-all ${
                  active
                    ? key === "ems"
                      ? "border-fegreen bg-fegreen-light/40 text-fegreen"
                      : "border-amber-400 bg-amber-50 text-amber-700"
                    : "border-gray-200 text-fegray hover:border-fegreen/50"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {cfg.label.split(" ")[0]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Features editor */}
      <div className="mb-4">
        <label className="text-[10px] uppercase tracking-wider text-fegray font-bold mb-2 block">
          Avantages affichés ({edit.features.length})
        </label>
        {edit.features.length > 0 && (
          <div className="space-y-2 mb-3">
            {edit.features.map((f, i) => (
              <div key={i} className="flex items-center gap-2 bg-fegray-light/60 rounded-xl px-2 py-1.5">
                <span className="text-fegray/40 cursor-grab">
                  <GripVertical className="w-3 h-3" />
                </span>
                <input
                  value={f}
                  onChange={(e) => {
                    const arr = [...edit.features];
                    arr[i] = e.target.value;
                    setEdit({ ...edit, features: arr });
                  }}
                  data-testid={`pack-feature-${pack.id}-${i}`}
                  className="flex-1 bg-white border border-gray-200 rounded-lg px-2 py-1 text-xs text-fegray-dark focus:border-fegreen focus:outline-none"
                />
                <button type="button" onClick={() => moveFeature(i, -1)} className="text-fegray hover:text-fegreen text-xs px-1">↑</button>
                <button type="button" onClick={() => moveFeature(i, 1)} className="text-fegray hover:text-fegreen text-xs px-1">↓</button>
                <button type="button" onClick={() => removeFeature(i)} className="text-red-400 hover:text-red-600" data-testid={`pack-feature-remove-${pack.id}-${i}`}>
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            value={newFeature}
            onChange={(e) => setNewFeature(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
            placeholder="Nouvel avantage… (ex : Suivi personnalisé)"
            data-testid={`pack-new-feature-${pack.id}`}
            className="flex-1 bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs text-fegray-dark focus:border-fegreen focus:outline-none"
          />
          <button
            type="button"
            onClick={addFeature}
            data-testid={`pack-add-feature-${pack.id}`}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-fegreen text-white text-xs font-bold hover:bg-fegreen-dark"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-fegray mb-4 cursor-pointer">
        <input
          type="checkbox"
          checked={edit.popular}
          onChange={(e) => setEdit({ ...edit, popular: e.target.checked })}
          data-testid={`pack-popular-${pack.id}`}
          className="accent-fegreen w-4 h-4"
        />
        Marquer comme &laquo; populaire &raquo; (badge sur le site)
      </label>

      <button
        onClick={submit}
        disabled={saving || !dirty}
        className="btn-green w-full disabled:opacity-50"
        data-testid={`save-pack-${pack.id}`}
      >
        {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Enregistrement…</> : dirty ? <><Save className="w-4 h-4" /> Enregistrer</> : "Aucun changement"}
      </button>
    </div>
  );
}
